import React, { useState } from 'react';
import { Compass, Sparkles, CheckCircle2 } from 'lucide-react';
import { CandleProduct } from '../types';
import { LuxuryModal } from './ui/LuxuryModal';

interface FragranceWorldModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddCustomToCart: (candle: CandleProduct) => void;
}

const TOP_NOTES = ['Crushed Cardamom', 'Wild Sea Salt', 'Orchid Blossom', 'Eucalyptus Fog', 'Damask Rose Petals'];
const HEART_NOTES = ['Australian Sandalwood', 'Highland French Lavender', 'Madagascar Bourbon Vanilla', 'Roasted Arabica', 'Smoked Oud'];
const BASE_NOTES = ['Amber Resin', 'Smoky Oak', 'White Cashmere', 'Charred Birch', 'Dark Patchouli'];

export const FragranceWorldModal: React.FC<FragranceWorldModalProps> = ({
  isOpen,
  onClose,
  onAddCustomToCart,
}) => {
  const [selectedTop, setSelectedTop] = useState(TOP_NOTES[0]);
  const [selectedHeart, setSelectedHeart] = useState(HEART_NOTES[0]);
  const [selectedBase, setSelectedBase] = useState(BASE_NOTES[0]);
  const [customName, setCustomName] = useState('My Bespoke Sanctuary');

  const handleCreateAndAdd = () => {
    const bespokeCandle: CandleProduct = {
      id: `bespoke-${Date.now()}`,
      name: customName.trim() || 'Bespoke Artisan Candle',
      tagline: 'Custom hand-blended formulation crafted by you & Rashmi.',
      description: `A unique personal blend combining top notes of ${selectedTop}, heart notes of ${selectedHeart}, and base notes of ${selectedBase}.`,
      topNotes: [selectedTop],
      heartNotes: [selectedHeart],
      baseNotes: [selectedBase],
      burnTime: '58 Hours',
      weight: '280g / 9.8 oz',
      waxType: '100% Pure Soy & Coconut Wax',
      wickType: 'Unbleached Organic Cotton Wick',
      mood: 'Personal Harmony & Intention',
      price: 78,
      colorHex: '#C49A63',
      flameColorHex: '#B5895A',
      accentColor: 'from-[#8C6239] to-[#76512F]',
      imageBg: 'linear-gradient(135deg, #F7F2E8 0%, #F3ECE2 100%)',
      vibeStory: 'A tailored fragrance profile hand-blended specifically for your quiet space.',
    };

    onAddCustomToCart(bespokeCandle);
    onClose();
  };

  return (
    <LuxuryModal
      isOpen={isOpen}
      onClose={onClose}
      subtitle="Scent Atelier"
      icon={<Compass className="w-3.5 h-3.5" />}
      ariaLabel="Craft Your Bespoke Fragrance"
      maxWidthClass="max-w-2xl sm:max-w-3xl"
    >
      <div className="space-y-6 sm:space-y-8">
        {/* Title */}
        <div>
          <h2 className="text-2xl sm:text-4xl font-serif text-[#3C2D22] font-normal leading-tight">
            Craft Your Bespoke Fragrance
          </h2>
          <p className="text-xs sm:text-sm text-[#6A5948] font-sans mt-1">
            Select your preferred olfactory notes below to compose your hand-poured custom creation.
          </p>
        </div>

        {/* Input Field: Custom Name */}
        <div className="space-y-2">
          <label className="text-[11px] font-sans uppercase tracking-[0.2em] text-[#B5895A] font-semibold block">
            Name Your Custom Candle
          </label>
          <input
            type="text"
            value={customName}
            onChange={(e) => setCustomName(e.target.value)}
            placeholder="e.g. My Bespoke Sanctuary"
            className="w-full bg-white border border-[#D8C29B] rounded-xl px-4 py-3 text-[#3C2D22] font-serif text-lg sm:text-xl placeholder-[#6A5948]/50 focus:outline-none focus:ring-2 focus:ring-[#B5895A]/40 focus:border-[#B5895A] transition-all shadow-xs"
          />
        </div>

        {/* 1. Top Note Selector */}
        <div className="space-y-3">
          <span className="text-[11px] font-sans uppercase tracking-[0.18em] text-[#6A5948] font-bold block">
            1. Select Top Note <span className="font-normal text-[#6A5948]/70">(First Impression)</span>
          </span>
          <div className="flex flex-wrap gap-2 sm:gap-2.5">
            {TOP_NOTES.map((note) => {
              const isSelected = selectedTop === note;
              return (
                <button
                  key={note}
                  type="button"
                  onClick={() => setSelectedTop(note)}
                  className={`px-4 py-2 rounded-full text-xs font-sans font-medium transition-all duration-200 cursor-pointer ${
                    isSelected
                      ? 'bg-[#C49A63] text-white shadow-sm ring-1 ring-[#B5895A]'
                      : 'bg-[#F3ECE2] border border-[#D8C29B]/80 text-[#6A5948] hover:bg-[#EAE0D2] hover:text-[#3C2D22] hover:-translate-y-0.5'
                  }`}
                >
                  {note}
                </button>
              );
            })}
          </div>
        </div>

        {/* 2. Heart Note Selector */}
        <div className="space-y-3">
          <span className="text-[11px] font-sans uppercase tracking-[0.18em] text-[#6A5948] font-bold block">
            2. Select Heart Note <span className="font-normal text-[#6A5948]/70">(Core Character)</span>
          </span>
          <div className="flex flex-wrap gap-2 sm:gap-2.5">
            {HEART_NOTES.map((note) => {
              const isSelected = selectedHeart === note;
              return (
                <button
                  key={note}
                  type="button"
                  onClick={() => setSelectedHeart(note)}
                  className={`px-4 py-2 rounded-full text-xs font-sans font-medium transition-all duration-200 cursor-pointer ${
                    isSelected
                      ? 'bg-[#C49A63] text-white shadow-sm ring-1 ring-[#B5895A]'
                      : 'bg-[#F3ECE2] border border-[#D8C29B]/80 text-[#6A5948] hover:bg-[#EAE0D2] hover:text-[#3C2D22] hover:-translate-y-0.5'
                  }`}
                >
                  {note}
                </button>
              );
            })}
          </div>
        </div>

        {/* 3. Base Note Selector */}
        <div className="space-y-3">
          <span className="text-[11px] font-sans uppercase tracking-[0.18em] text-[#6A5948] font-bold block">
            3. Select Base Note <span className="font-normal text-[#6A5948]/70">(Lingering Memory)</span>
          </span>
          <div className="flex flex-wrap gap-2 sm:gap-2.5">
            {BASE_NOTES.map((note) => {
              const isSelected = selectedBase === note;
              return (
                <button
                  key={note}
                  type="button"
                  onClick={() => setSelectedBase(note)}
                  className={`px-4 py-2 rounded-full text-xs font-sans font-medium transition-all duration-200 cursor-pointer ${
                    isSelected
                      ? 'bg-[#C49A63] text-white shadow-sm ring-1 ring-[#B5895A]'
                      : 'bg-[#F3ECE2] border border-[#D8C29B]/80 text-[#6A5948] hover:bg-[#EAE0D2] hover:text-[#3C2D22] hover:-translate-y-0.5'
                  }`}
                >
                  {note}
                </button>
              );
            })}
          </div>
        </div>

        {/* Summary Card */}
        <div className="p-5 bg-[#F3ECE2] rounded-2xl border border-[#D8C29B]/80 space-y-2 shadow-xs">
          <div className="flex justify-between items-center text-sm font-serif font-bold text-[#3C2D22]">
            <span>{customName || 'Bespoke Artisan Candle'}</span>
            <span className="text-[#8C6239]">$78</span>
          </div>
          <p className="text-xs text-[#6A5948] italic">
            Formulation: {selectedTop} • {selectedHeart} • {selectedBase}
          </p>
        </div>

        {/* Large Rounded CTA Button */}
        <button
          type="button"
          onClick={handleCreateAndAdd}
          className="w-full py-4 rounded-full bg-gradient-to-r from-[#8C6239] to-[#76512F] text-white font-sans text-xs font-bold uppercase tracking-[0.2em] shadow-md hover:bg-[#76512F] hover:shadow-lg transition-all duration-200 flex items-center justify-center space-x-2 cursor-pointer hover:scale-[1.005] active:scale-[0.995] focus:outline-none focus:ring-2 focus:ring-[#B5895A]"
        >
          <Sparkles className="w-4 h-4" />
          <span>Hand-Blend & Add to Bag — $78</span>
        </button>
      </div>
    </LuxuryModal>
  );
};
